import type { Geometry3D } from '@ikegps/core-measurement';
import type {
	CalibrationJSONObject,
	CaptureFileJSONObject,
	CollectionSummaryJSONObject,
	CollectionVariantRef,
} from '@ikegps/core-api';
import { makeObservable, observable, type ObservableMap } from 'mobx';
import type { IMap } from '../ike.map';
import type { CollectionModel } from './collection.data';
import type { IkeCollectionLoader } from './collection.loader';
import type { CollectionModelState, ModelFieldState } from './collection.state';
import type { CollectionModelDrawable } from './drawable/drawable';
import type { CollectionModelDrawableState } from './drawable/drawable.state';

function cloneDrawableStates(
	source: ObservableMap<string, CollectionModelDrawableState>
): ObservableMap<string, CollectionModelDrawableState> {
	const drawableStates = observable.map<string, CollectionModelDrawableState>();
	for (const [captureId, drawableState] of Array.from(source.entries())) {
		drawableStates.set(captureId, drawableState.clone());
	}
	return drawableStates;
}

export abstract class CollectionModelStateProxy implements CollectionModelState {
	frozen = false;

	get loader(): IkeCollectionLoader {
		return this.internalState.loader;
	}
	get id(): string {
		return this.internalState.id;
	}
	get instanceId(): string {
		return this.internalState.instanceId;
	}
	get uuid(): string {
		return this.internalState.uuid;
	}
	get revision(): string {
		return this.internalState.revision;
	}
	get jobId(): string {
		return this.internalState.jobId;
	}
	get updatedAt(): Date {
		return this.internalState.updatedAt;
	}
	get updatedBy(): string {
		return this.internalState.updatedBy;
	}
	get createdAt(): Date {
		return this.internalState.createdAt;
	}
	get collectedAt(): Date {
		return this.internalState.collectedAt;
	}
	get collectedBy(): string {
		return this.internalState.collectedBy;
	}
	get collectedWith(): string {
		return this.internalState.collectedWith;
	}
	get assignedTo(): string {
		return this.internalState.assignedTo;
	}
	get variantType(): string | undefined {
		return this.internalState.variantType;
	}
	get variantTypeName(): string | undefined {
		return this.internalState.variantTypeName;
	}
	get variantForUuid(): string | undefined {
		return this.internalState.variantForUuid;
	}
	get variants(): CollectionVariantRef[] | undefined {
		return this.internalState.variants;
	}

	abstract fields: IMap<ModelFieldState>;
	abstract geometries: IMap<Geometry3D>;

	/**
	 * Drawable state is NOT proxied -- it starts as a full copy of the original state.
	 */
	@observable drawableStates = observable.map<string, CollectionModelDrawableState>();

	private internalState: CollectionModelState;

	constructor(state: CollectionModelState) {
		makeObservable(this);

		this.internalState = state;
		this.resetDrawables();
	}

	get isDirty(): boolean {
		return (this.fields.size ?? 0) > 0 || this.drawableStateHasChanged();
	}

	protected drawableStateHasChanged(): boolean {
		// Only compare the drawable states which have drawables in them,
		// because CollectionModelState may or may not have drawableStates for captures with no drawables,
		// but it always has drawable states for captures with drawables.

		const drawableStates = Array.from(this.drawableStates.values()).filter((ds) => !ds.empty);
		const internalDrawableStates = Array.from(this.internalState.drawableStates.values()).filter((ds) => !ds.empty);

		const keys = Array.from(drawableStates.keys());
		const internalKeys = Array.from(internalDrawableStates.keys());

		if (keys.length !== internalKeys.length) {
			return true;
		}
		if (keys.some((key) => !internalKeys.includes(key))) {
			return true;
		}
		if (internalKeys.some((key) => !keys.includes(key))) {
			return true;
		}

		for (const drawableState of drawableStates) {
			const originalDrawableState = internalDrawableStates.find((ds) => ds.captureId === drawableState.captureId);
			if (!originalDrawableState) {
				return true;
			}
			if (!drawableState.equals(originalDrawableState)) {
				return true;
			}
		}
		return false;
	}

	reset() {
		this.fields.clear();
		this.geometries.clear();
		this.resetDrawables();
	}

	protected resetDrawables() {
		this.drawableStates.clear();

		for (const [captureId, drawableState] of Array.from(
			cloneDrawableStates(this.internalState.drawableStates).entries()
		)) {
			this.drawableStates.set(captureId, drawableState);
		}
	}

	getFieldValue(fieldId: string): ModelFieldState {
		if (this.fields.has(fieldId)) {
			return this.fields.get(fieldId);
		}

		return this.internalState.getFieldValue(fieldId);
	}

	setFieldValue<T extends ModelFieldState>(fieldId: string, state: T): void {
		let internalValue = this.internalState.getFieldValue(fieldId);

		// Normalize null/undefined — both mean "no value" and should be treated as equivalent.
		// Without this, clearing or copying fields that were never set (undefined in saved state)
		// produces null, which JSON.stringify treats differently from undefined.
		const normalizedState = state ?? null;
		const normalizedInternal = internalValue ?? null;

		if (JSON.stringify(normalizedState) === JSON.stringify(normalizedInternal)) {
			this.fields.delete(fieldId);
		} else {
			this.fields.set(fieldId, state);
		}
	}

	hasFieldValue(fieldId: string): boolean {
		return this.getFieldValue(fieldId) != null;
	}

	getDrawable(captureId: string, drawableId: string): CollectionModelDrawable {
		if (this.drawableStates.get(captureId)?.hasDrawable(drawableId)) {
			return this.drawableStates.get(captureId).getDrawable(drawableId);
		}
		throw new Error(`Drawable ${drawableId} (capture ${captureId}) not found in proxy state`);
	}

	getCapture(captureId: string): CaptureFileJSONObject {
		return this.internalState.getCapture(captureId);
	}

	getCalibration(calibrationId: string): CalibrationJSONObject {
		return this.internalState.getCalibration(calibrationId);
	}

	getGeometry(captureId: string): Geometry3D {
		return this.internalState.getGeometry(captureId);
	}

	getCollection(uuid: string): CollectionSummaryJSONObject | null {
		return this.internalState.getCollection(uuid);
	}

	/**
	 * @deprecated This method can create race conditions; use `getCollectionModelAsync()` instead.
	 */
	getCollectionModel(collectionId: string): CollectionModel {
		return this.internalState.getCollectionModel(collectionId);
	}

	async getCollectionModelAsync(collectionId: string): Promise<CollectionModel> {
		return await this.internalState.getCollectionModelAsync(collectionId);
	}

	freeze(): void {
		throw new Error('Cannot freeze edit state');
	}
}
